import { Fragment, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Inbox,
  Database,
  ArrowRight,
  Menu,
  X,
  ExternalLink,
  Rss,
  Brain,
  Library,
  FileText,
  LogOut,
  TrendingUp,
  Palette,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";
import { RefreshButton } from "@/components/shared/RefreshButton";
import { ADMIN } from "@/constants/testIds";

const NAV = [
  { key: "dashboard", to: "/admin",          label: "لوحة القيادة",   Icon: LayoutDashboard, end: true },
  { key: "review",    to: "/admin/review",   label: "مراجعة الفرص",  Icon: Inbox },
  { key: "feed",      to: "/admin/feed",     label: "مراقبة المصادر", Icon: Rss },
  { key: "ai-queue",  to: "/admin/ai-queue", label: "طابور الذكاء",   Icon: Brain },
  { key: "sources",   to: "/admin/sources",  label: "المصادر",       Icon: Database },
  { key: "coverage",  to: "/admin/coverage", label: "تغطية الأسواق", Icon: TrendingUp },
  { key: "knowledge", to: "/admin/knowledge", label: "مكتبة المعرفة", Icon: Library },
  { key: "content",   to: "/admin/content",   label: "المحتوى",       Icon: FileText },
  { key: "appearance", to: "/admin/appearance", label: "المظهر والأسواق", Icon: Palette },
  // "الإعدادات" (settings) sidebar link intentionally hidden in Sprint 28a
  // until the runtime Brand Management sprint lands (Sprint B). The route
  // still resolves for deep-links but no longer appears in navigation.
];

const PAGE_TITLES = {
  "/admin":          "لوحة القيادة",
  "/admin/review":   "مراجعة الفرص",
  "/admin/feed":     "مراقبة المصادر",
  "/admin/ai-queue": "طابور الذكاء",
  "/admin/sources":  "المصادر",
  "/admin/coverage": "تغطية الأسواق",
  "/admin/content":  "المحتوى",
  "/admin/appearance": "المظهر والأسواق",
  "/admin/settings": "الإعدادات",
};const resolveTitle = (pathname) => {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (pathname.startsWith("/admin/opportunities/")) return "محرِّر الفرصة";
  return "لوحة الإدارة";
};

const SidebarBody = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await logout();
    toast.success("تم تسجيل الخروج");
    navigate("/", { replace: true });
  };

  return (
  <>
    <div className="flex items-center gap-3 px-6 pb-8 pt-6">
      <Logo variant="mark" size="sm" />
      <div className="ms-1 flex flex-col leading-none">
        <span className="text-base font-bold tracking-tight text-ink-900">فرصالوجيا</span>
        <span className="mt-1 text-[9px] font-semibold uppercase tracking-[0.24em] text-intel-700">Admin</span>
      </div>
    </div>

    <nav aria-label="التنقل الإداري" className="flex-1 space-y-1 px-3">
      {NAV.map(({ key, to, label, Icon, end }) => (
        <NavLink
          key={key}
          to={to}
          end={end}
          onClick={onNavigate}
          data-testid={ADMIN.navItem(key)}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive
                ? "bg-intel-800 text-white"
                : "text-ink-700 hover:bg-surface-100 hover:text-ink-900"
            }`
          }
        >
          <Icon className="h-4 w-4" strokeWidth={1.75} />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>

    <div className="mt-4 space-y-1 border-t border-surface-200 p-3">
      {user && (
        <div
          data-testid="admin-current-user"
          className="rounded-md bg-surface-50 px-3 py-2 text-[11.5px] leading-tight"
        >
          <div className="truncate font-medium text-ink-900" title={user.email}>{user.email}</div>
          <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-intel-800">
            {user.role === "admin" ? "Admin" : user.role === "editor" ? "Editor" : "User"}
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={handleSignOut}
        data-testid="admin-sign-out"
        className="flex w-full items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium text-ink-500 transition-colors hover:bg-surface-100 hover:text-ink-900"
      >
        <span className="inline-flex items-center gap-2">
          <LogOut className="h-4 w-4" strokeWidth={1.75} />
          تسجيل الخروج
        </span>
      </button>
      <Link
        to="/"
        onClick={onNavigate}
        data-testid={ADMIN.backToSite}
        className="flex items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium text-ink-500 transition-colors hover:bg-surface-100 hover:text-ink-900"
      >
        <span className="inline-flex items-center gap-2">
          <ExternalLink className="h-4 w-4" strokeWidth={1.75} />
          العودة إلى الموقع
        </span>
        <ArrowRight className="h-4 w-4 -scale-x-100" strokeWidth={1.75} />
      </Link>
    </div>
  </>
  );
};

export const AdminLayout = ({ children }) => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const title = resolveTitle(pathname);

  // Real identity from the existing auth session — never hard-coded.
  const displayName = (user?.full_name?.trim()) || user?.email || "";
  const roleLabel = user?.role === "super_admin"
    ? "مشرِف عام"
    : user?.role === "admin"
      ? "مسؤول الإدارة"
      : user?.role === "editor"
        ? "محرِّر"
        : "مسؤول الإدارة";
  const initials = (() => {
    if (!displayName) return "؟";
    // Prefer full_name initials. For email fallback, use the local-part first char.
    const source = user?.full_name?.trim() ? user.full_name.trim() : (user?.email || "").split("@")[0];
    const parts = source.split(/[\s._-]+/).filter(Boolean);
    if (parts.length === 0) return "؟";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  })();

  return (
    <div data-testid={ADMIN.root} className="min-h-screen overflow-x-clip bg-surface-50 text-ink-900">
      {/* Sidebar — desktop */}
      <aside
        aria-label="الشريط الجانبي للإدارة"
        className="fixed inset-y-0 end-0 z-40 hidden w-64 flex-col border-s border-surface-200 bg-white lg:flex"
        data-testid={ADMIN.sidebar}
      >
        <SidebarBody />
      </aside>

      {/* Sidebar — mobile drawer */}
      {mobileOpen && (
        <Fragment>
          <div
            className="fixed inset-0 z-40 bg-ink-900/40 lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside
            className="fixed inset-y-0 end-0 z-50 flex w-72 flex-col border-s border-surface-200 bg-white lg:hidden"
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="absolute start-3 top-3 inline-flex h-10 w-10 items-center justify-center rounded-md text-ink-500 hover:bg-surface-100 hover:text-ink-900"
              aria-label="إغلاق"
            >
              <X className="h-5 w-5" strokeWidth={1.75} />
            </button>
            <SidebarBody onNavigate={() => setMobileOpen(false)} />
          </aside>
        </Fragment>
      )}

      {/* Top bar + content */}
      <div className="lg:pe-64">
        <header
          className="sticky top-0 z-30 border-b border-surface-200 bg-white/95 backdrop-blur-sm"
          data-testid={ADMIN.topbar}
        >
          <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-md text-ink-700 hover:bg-surface-100 lg:hidden"
                aria-label="فتح الشريط الجانبي"
                data-testid={ADMIN.mobileMenuBtn}
              >
                <Menu className="h-5 w-5" strokeWidth={1.75} />
              </button>
              <h1
                data-testid={ADMIN.pageTitle}
                className="text-base font-semibold tracking-tight text-ink-900 sm:text-lg"
              >
                {title}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <RefreshButton />
              <div className="hidden flex-col items-end leading-tight sm:flex">
                <span
                  data-testid="admin-topbar-name"
                  className="max-w-40 truncate text-xs font-medium text-ink-900"
                  title={displayName}
                >
                  {displayName || "—"}
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-widest text-ink-500">
                  {roleLabel}
                </span>
              </div>
              <span
                data-testid="admin-topbar-avatar"
                aria-label={displayName || "مسؤول الإدارة"}
                title={displayName || roleLabel}
                className="inline-flex h-9 min-w-9 items-center justify-center rounded-full bg-intel-800 px-2 text-[11px] font-semibold uppercase tracking-wider text-white"
              >
                {initials}
              </span>
            </div>
          </div>
        </header>
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
